import { formatTime } from "./dateUtils";

// 1. Generate Emoji Grid for FinWord
function generateEmojiGrid(guesses: readonly string[], solution: string): string {
  return guesses
    .map((guess) => {
      const rowEmojis: string[] = [];
      const matched = new Array(5).fill(false);
      const targetLetters = solution.split("");

      // Pass 1: Green letters
      for (let i = 0; i < 5; i++) {
        if (guess[i] === solution[i]) {
          rowEmojis[i] = "🟩";
          matched[i] = true;
          targetLetters[i] = "";
        }
      }

      // Pass 2: Yellow / Gray letters
      for (let i = 0; i < 5; i++) {
        if (rowEmojis[i]) continue;
        const index = targetLetters.indexOf(guess[i]);
        if (index !== -1) {
          rowEmojis[i] = "🟨";
          targetLetters[index] = "";
        } else {
          rowEmojis[i] = "⬛";
        }
      }

      return rowEmojis.join("");
    })
    .join("\n");
}

// 2. Generate FinWord Text Share
export function generateFinWordShare(
  dayNumber: number,
  guesses: readonly string[],
  solution: string,
  won: boolean,
  streak: number,
  completionTime: number
): string {
  const attemptsText = won ? `${guesses.length}/6` : "X/6";
  const grid = generateEmojiGrid(guesses, solution);
  
  return `🎯 FinWord #${dayNumber} ${attemptsText}
  
${grid}

🔥 Streak: ${streak} days
⏱️ Time: ${formatTime(completionTime)}

Play today's challenge → https://financelives.com/daily/word`;
}

// 3. Generate Quiz Text Share
export function generateQuizShare(
  correctCount: number,
  totalCount: number,
  streak: number,
  bestCategory: string
): string {
  const percentage = Math.round((correctCount / totalCount) * 100);
  return `📊 Finance Quiz of the Day
  
Score: ${correctCount}/${totalCount} correct (${percentage}%)
🔥 Streak: ${streak} days
📈 Key Area: ${bestCategory}

Test your knowledge → https://financelives.com/daily/quiz`;
}

// 4. Generate Combined Daily Share
export function generateCombinedShare(
  dateFormatted: string,
  wordCompleted: boolean,
  wordAttempts: number,
  wordWon: boolean,
  quizCompleted: boolean,
  quizScore: number,
  streak: number
): string {
  const wordStatus = wordCompleted ? (wordWon ? `Won in ${wordAttempts}/6` : "Lost") : "Incomplete";
  const quizStatus = quizCompleted ? `${quizScore}/4` : "Incomplete";

  return `🎯 FinanceLives Daily Challenge
📅 ${dateFormatted}

🔤 FinWord: ${wordStatus}
🧠 Quiz: ${quizStatus}

🔥 ${streak} day streak!

Try today's challenge → https://financelives.com/daily`;
}

/**
 * Procedurally draws a high-resolution 1200x630 share card on a canvas element
 * and returns it as a PNG Data URL or blob for social sharing.
 */
export function generateShareCardCanvas(
  title: string,
  subtitle: string,
  statsLabel: string,
  statsVal: string,
  extraDetails?: string[]
): string {
  if (typeof document === "undefined") return "";

  const canvas = document.createElement("canvas");
  canvas.width = 1200;
  canvas.height = 630;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  // 1. Draw Background Gradient
  const grad = ctx.createLinearGradient(0, 0, 1200, 630);
  grad.addColorStop(0, "#0b0f19");
  grad.addColorStop(1, "#131a2c");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1200, 630);

  // 2. Draw Decorative Shapes/Glows
  ctx.fillStyle = "rgba(16, 185, 129, 0.03)";
  ctx.beginPath();
  ctx.arc(1000, 150, 300, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "rgba(59, 130, 246, 0.02)";
  ctx.beginPath();
  ctx.arc(200, 500, 400, 0, Math.PI * 2);
  ctx.fill();

  // 3. Draw Branding Logo
  ctx.fillStyle = "#10b981"; // Emerald Primary
  ctx.beginPath();
  ctx.arc(80, 80, 20, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 28px sans-serif";
  ctx.fillText("FinanceLives", 115, 88);

  ctx.fillStyle = "#94a3b8";
  ctx.font = "20px sans-serif";
  ctx.fillText("DAILY CHALLENGE", 300, 86);

  // Divider Line
  ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(60, 130);
  ctx.lineTo(1140, 130);
  ctx.stroke();

  // 4. Draw Main Content Card
  ctx.fillStyle = "#182235";
  ctx.beginPath();
  ctx.roundRect(80, 180, 1040, 380, 24);
  ctx.fill();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.04)";
  ctx.stroke();

  // Text inside the Card
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 44px sans-serif";
  ctx.fillText(title, 130, 260);

  ctx.fillStyle = "#94a3b8";
  ctx.font = "26px sans-serif";
  ctx.fillText(subtitle, 130, 310);

  // Big Metric
  ctx.fillStyle = "#10b981";
  ctx.font = "bold 100px sans-serif";
  ctx.fillText(statsVal, 130, 460);

  ctx.fillStyle = "#94a3b8";
  ctx.font = "bold 22px sans-serif";
  ctx.fillText(statsLabel.toUpperCase(), 130, 510);

  // Extra Details Box (e.g. Completes list)
  if (extraDetails && extraDetails.length > 0) {
    ctx.fillStyle = "rgba(255, 255, 255, 0.03)";
    ctx.beginPath();
    ctx.roundRect(650, 220, 420, 290, 16);
    ctx.fill();

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 22px sans-serif";
    ctx.fillText("Activity Summary", 690, 270);

    ctx.font = "20px sans-serif";
    extraDetails.forEach((detail, index) => {
      ctx.fillStyle = detail.includes("Incomplete") ? "#64748b" : "#f8fafc";
      const bullet = detail.includes("Incomplete") ? "○" : "✓";
      ctx.fillText(`${bullet} ${detail}`, 690, 320 + index * 40);
    });
  }

  return canvas.toDataURL("image/png");
}
